/**
 * Kant-en-klare spraakclips op de tablet (ElevenLabs-renders, door de API geserveerd).
 *
 * Spec: specs/20261020-spraakmeldingen-elevenlabs/spec.md §5.6, §5.7, FR-1.4, FR-1.5
 * Plan: fase 1 · Taak: T091 (tests: services/__tests__/VoiceCache.test.ts, T070)
 *
 * ── WAT DIT IS ──────────────────────────────────────────────────────────────
 *
 * De naast `SoundCache` staande cache voor gesproken clips. Twee bronnen:
 *
 *   1. De VOORLAADLIJST `GET /api/v1/resident/voice-prompts?lang=` — de `preload`-prompts van
 *      de catalogus (deurbel, videofoon) die voor de site van dit toestel klaarstaan. Bij
 *      opstart en bij `profile_refresh` opgehaald, in de app-taal. De lijst zelf blijft in
 *      localStorage staan, zodat een tablet die offline herstart zijn clips nog terugvindt.
 *   2. `payload.tts.audio` van een melding (een vrije tekst, of een catalogussleutel).
 *
 * ── DE REGELS (elk ervan ging op deze vloot al eens fout) ───────────────────
 *
 *   • Cache Storage `caro-voices-v1`, sleutel `${key}-${checksum}`: inhoudsgeadresseerd, dus
 *     invalidatie is gratis.
 *   • Alleen `audio/*`. De app-host antwoordt op elk pad met 200 + `index.html`.
 *   • De sha-256 van de bytes moet de checksum zijn; anders weg. Zonder `crypto.subtle` (oude
 *     WebView) alleen `audio/*` + niet-lege bytes.
 *   • Ophalen ALLEEN via de geauthenticeerde ophaler van de app (de ene `createApiClient()`),
 *     met een ABSOLUTE API-URL. Nooit een kale relatieve `fetch('/api/…')`: die gaat naar de
 *     app-host (zie `SoundCache.ts`, v1-cache vergiftigd op 2026-09-24).
 *   • Time-out via `timeoutSignal()`, nooit `AbortSignal.timeout` (Chromium 69–84).
 *   • LRU van 200; de `preload`-clips worden nooit verdrongen.
 *   • Op een APK ≥ 1.0.6 gaan de bytes na de download ook naar de native cache
 *     (`KeepAlive.cacheAudio`), zodat `MediaPlayer` ze zonder WebView-autoplay speelt.
 *
 * Werpt nooit. Een clip die er niet is, is gewoon de volgende trede van de ladder
 * (Android-TTS → oud geluid → tekst).
 */

import { API_ENDPOINTS } from '@caro/shared/constants';
import { timeoutSignal } from '../utils/timeoutSignal';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import { isAudioType, cacheOpener, objectUrlOwner, sha256Hex, bytesOf } from './audioCacheCore';

export const CACHE_NAME = 'caro-voices-v1';
export const MAX_ENTRIES = 200;
const NETWORK_TIMEOUT_MS = 5000;
const INDEX_STORAGE_KEY = 'caro_voice_prompts_v1';
const LRU_STORAGE_KEY = 'caro_voices_lru_v1';
const PROMPTS_URL: string =
  ((API_ENDPOINTS as unknown as Record<string, unknown>) || {}).RESIDENT_VOICE_PROMPTS as string
  || '/api/v1/resident/voice-prompts';

/** Wat de tablet van een clip moet weten om hem te halen en te spelen. */
export interface VoiceClipRef {
  /** Catalogussleutel of `h:<render_key>`. */
  key: string;
  lang?: string;
  /** Pad onder de API, bv. `/api/v1/resident/voice-renders/123/audio`. */
  url: string;
  checksum: string;
  durationMs?: number | null;
  mixed?: boolean;
}

/** Een regel van de voorlaadlijst. */
export interface VoicePromptEntry extends VoiceClipRef {
  lang: string;
  /** De gesproken tekst — voor de Android-TTS-terugval als de clip niet speelt. */
  text: string;
  preload: boolean;
}

export interface VoiceClip {
  /** `blob:`-URL voor de WebView. */
  src: string;
  blob: Blob;
  /** `${key}-${checksum}`, de sleutel voor de native cache. */
  contentKey: string;
  origin: 'cache' | 'network';
}

export interface VoiceCacheTransport {
  /** Absolute API-basis (`VITE_API_URL`). */
  baseUrl: string;
  /** JSON via de geauthenticeerde client (relatief pad, de client kent zijn basis). */
  getJson: (url: string) => Promise<unknown>;
  /** Bytes via de geauthenticeerde client; krijgt een ABSOLUTE URL en een time-outsignaal. */
  getBlob: (url: string, init: { signal?: AbortSignal }) => Promise<Blob | null>;
}

let transport: VoiceCacheTransport | null = null;
let lastLang: string | null = null;
const inflight = new Map<string, Promise<void>>();
const opener = cacheOpener(CACHE_NAME);
const urls = objectUrlOwner();

export function configure(t: VoiceCacheTransport | null): void {
  transport = t;
}

/** Maak een API-pad absoluut. Een al absolute URL blijft wat hij is. */
export function absoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = transport ? String(transport.baseUrl || '').replace(/\/+$/, '') : '';
  return `${base}${url.charAt(0) === '/' ? '' : '/'}${url}`;
}

// ── Persistente toestand (localStorage, werpt nooit) ─────────────────────────

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* geen opslag of vol — dan geldt het alleen voor deze sessie */
  }
}

let index: Record<string, VoicePromptEntry[]> | null = null;
let lru: Record<string, { t: number; preload?: boolean }> | null = null;
let clock = 0;

function getIndex(): Record<string, VoicePromptEntry[]> {
  if (!index) index = readJson<Record<string, VoicePromptEntry[]>>(INDEX_STORAGE_KEY, {});
  return index;
}

function getLru(): Record<string, { t: number; preload?: boolean }> {
  if (!lru) {
    lru = readJson<Record<string, { t: number; preload?: boolean }>>(LRU_STORAGE_KEY, {});
    for (const v of Object.values(lru)) if (v && v.t > clock) clock = v.t;
  }
  return lru;
}

function touch(cacheKey: string, preload?: boolean): void {
  const map = getLru();
  const prev = map[cacheKey];
  clock += 1;
  map[cacheKey] = { t: clock, preload: !!(preload || (prev && prev.preload)) };
  writeJson(LRU_STORAGE_KEY, map);
}

// ── Sleutels ─────────────────────────────────────────────────────────────────

/** Een sleutel die een cachepad en een bestandsnaam kan zijn (`h:` → `h_`). */
function safeKey(key: string): string {
  return String(key || '').replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 120) || 'voice';
}

function cacheKeyFor(ref: VoiceClipRef): string {
  return `/caro-voice/${safeKey(ref.key)}-${ref.checksum}`;
}

function contentKeyFor(ref: VoiceClipRef): string {
  return `${safeKey(ref.key)}-${ref.checksum}`;
}

function isValidRef(ref: unknown): ref is VoiceClipRef {
  const r = ref as VoiceClipRef;
  return !!r && typeof r.key === 'string' && !!r.key && typeof r.url === 'string' && !!r.url
    && typeof r.checksum === 'string' && /^[0-9a-f]{16,128}$/i.test(r.checksum);
}

/** `true` = deze bytes mogen een clip zijn. */
async function acceptable(blob: Blob | null | undefined, ref: VoiceClipRef): Promise<boolean> {
  if (!blob || !blob.size || !isAudioType(blob.type)) return false;
  const actual = await sha256Hex(blob);
  // `null` = niet te controleren (geen crypto.subtle): dan volstaat audio/* + lengte.
  return actual === null || actual.toLowerCase() === ref.checksum.toLowerCase();
}

function objectUrl(blob: Blob): string | null {
  try {
    return urls.own(URL.createObjectURL(blob));
  } catch {
    return null;
  }
}

async function prune(cache: Cache): Promise<void> {
  try {
    const keys = await cache.keys();
    if (keys.length <= MAX_ENTRIES) return;
    const map = getLru();
    const protectedKeys = new Set<string>();
    for (const list of Object.values(getIndex())) {
      for (const e of list || []) if (e && e.preload && isValidRef(e)) protectedKeys.add(cacheKeyFor(e));
    }
    const pathOf = (k: Request) => {
      try {
        return new URL(k.url, 'http://x').pathname;
      } catch {
        return k.url;
      }
    };
    const evictable = keys
      .map((k) => ({ path: pathOf(k) }))
      .filter((k) => !protectedKeys.has(k.path) && !(map[k.path] && map[k.path].preload))
      .sort((a, b) => ((map[a.path] && map[a.path].t) || 0) - ((map[b.path] && map[b.path].t) || 0));
    let excess = keys.length - MAX_ENTRIES;
    for (const k of evictable) {
      if (excess <= 0) break;
      // eslint-disable-next-line no-await-in-loop
      await cache.delete(k.path);
      delete map[k.path];
      excess -= 1;
    }
    writeJson(LRU_STORAGE_KEY, map);
  } catch {
    /* opruimen is onderhoud, geen functionaliteit */
  }
}

function cacheNatively(contentKey: string, blob: Blob): void {
  try {
    if (!KeepAliveBridge.supportsNativeAudio()) return;
    void KeepAliveBridge.cacheAudioBlob(contentKey, blob).catch(() => {
      /* de WebView speelt hem dan; het native pad haalt de bytes later zelf */
    });
  } catch {
    /* APK 1.0.5 of geen brug */
  }
}

// ── Publiek ──────────────────────────────────────────────────────────────────

/**
 * Zoek een speelbare clip: eerst de cache, dan het netwerk (geauthenticeerd, absoluut, met
 * time-out). `null` = er is geen clip (de ladder gaat naar de volgende trede). Werpt nooit.
 */
export async function resolveClip(
  ref: VoiceClipRef | null | undefined,
  opts: { preload?: boolean } = {},
): Promise<VoiceClip | null> {
  if (!isValidRef(ref)) return null;
  const cacheKey = cacheKeyFor(ref);
  const contentKey = contentKeyFor(ref);
  const cache = await opener.open();

  if (cache) {
    try {
      const hit = await cache.match(cacheKey);
      if (hit) {
        const blob = await hit.blob();
        if (await acceptable(blob, ref)) {
          const src = objectUrl(blob);
          if (src) {
            touch(cacheKey, opts.preload);
            return { src, blob, contentKey, origin: 'cache' };
          }
        } else {
          // Vergiftigd of kapot: weg, anders blijft dit toestel erop vastlopen.
          try {
            await cache.delete(cacheKey);
          } catch {
            /* dan lezen we hem nooit meer als geldig */
          }
        }
      }
    } catch {
      /* een kapotte cache is geen reden om niet te downloaden */
    }
  }

  // Zonder geauthenticeerde ophaler geen download — en BEWUST geen kale fetch.
  if (!transport) return null;
  let blob: Blob | null = null;
  try {
    blob = await transport.getBlob(absoluteUrl(ref.url), { signal: timeoutSignal(NETWORK_TIMEOUT_MS) });
  } catch {
    return null;
  }
  if (!(await acceptable(blob, ref))) return null;
  const bytes = blob as Blob;
  if (cache) {
    try {
      // De BYTES in de Response, niet de Blob zelf: zo leest elke implementatie hem terug.
      const body = await bytesOf(bytes);
      await cache.put(cacheKey, new Response(body, { headers: { 'content-type': bytes.type } }));
      touch(cacheKey, opts.preload);
      await prune(cache);
    } catch {
      /* geen ruimte of quotum — afspelen kan nog steeds */
    }
  }
  cacheNatively(contentKey, bytes);
  const src = objectUrl(bytes);
  return src ? { src, blob: bytes, contentKey, origin: 'network' } : null;
}

/** De voorlaadregel voor (sleutel, taal), of `null`. Leest ook na een herstart (localStorage). */
export function lookup(key: string, lang: string): VoicePromptEntry | null {
  const list = getIndex()[String(lang || '')];
  if (!Array.isArray(list)) return null;
  return list.find((e) => e && e.key === key && isValidRef(e)) || null;
}

/**
 * Haal de voorlaadlijst in deze taal en zet elke clip in de cache. Werpt nooit; een mislukte
 * lijst laat de vorige staan.
 */
export function preload(lang: string): Promise<void> {
  const l = String(lang || 'nl');
  lastLang = l;
  const running = inflight.get(l);
  if (running) return running;
  const job = (async () => {
    if (!transport) return;
    let res: any;
    try {
      res = await transport.getJson(`${PROMPTS_URL}?lang=${encodeURIComponent(l)}`);
    } catch {
      return;
    }
    const raw = res && Array.isArray(res.prompts) ? res.prompts : null;
    if (!raw) return;
    const list: VoicePromptEntry[] = raw
      .filter((e: unknown) => isValidRef(e))
      .map((e: any) => ({
        key: String(e.key),
        lang: String(e.lang || l),
        text: typeof e.text === 'string' ? e.text : '',
        url: String(e.url),
        checksum: String(e.checksum),
        durationMs: typeof e.durationMs === 'number' ? e.durationMs : null,
        mixed: e.mixed === true,
        preload: e.preload !== false,
      }));
    const idx = getIndex();
    idx[l] = list;
    writeJson(INDEX_STORAGE_KEY, idx);
    for (const entry of list) {
      // eslint-disable-next-line no-await-in-loop
      const clip = await resolveClip(entry, { preload: entry.preload });
      if (clip) urls.release(clip.src);
    }
  })().finally(() => {
    inflight.delete(l);
  });
  inflight.set(l, job);
  return job;
}

/** Opnieuw voorladen in de laatst gebruikte taal (`profile_refresh`). */
export function refresh(): Promise<void> {
  return lastLang ? preload(lastLang) : Promise.resolve();
}

export function releaseObjectUrl(url: string | null | undefined): void {
  urls.release(url);
}

/** Alleen voor tests: vergeet de geheugenkopie (alsof de app herstart). */
export function __forgetMemory(): void {
  index = null;
  lru = null;
  clock = 0;
  lastLang = null;
  inflight.clear();
}

/** Alleen voor tests. */
export async function __clear(): Promise<void> {
  __forgetMemory();
  transport = null;
  opener.reset();
  try {
    localStorage.removeItem(INDEX_STORAGE_KEY);
    localStorage.removeItem(LRU_STORAGE_KEY);
  } catch {
    /* niets te wissen */
  }
  try {
    if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME);
  } catch {
    /* niets te wissen */
  }
  urls.releaseAll();
}
